import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Eye, Send, Wallet } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function CuentaCorrientePanel({ tipo }) {
  const esCliente = tipo === "cliente";
  const endpoint = esCliente ? "/cuenta-corriente/clientes" : "/cuenta-corriente/proveedores";
  const pagoEndpoint = esCliente ? "/cobros/por-cliente" : "/pagos/por-proveedor";

  const [list, setList] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagoTarget, setPagoTarget] = useState(null);
  const [cuentasBanco, setCuentasBanco] = useState([]);
  const [pagoForm, setPagoForm] = useState({
    contacto_rut: "",
    monto: "",
    cuenta_bancaria_id: "",
    fecha: todayISO(),
    medio_pago: "transferencia",
    n_doc: "",
    glosa: "",
  });
  const [pagoSaving, setPagoSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setList(data.contactos || []);
      setTotales(data.totales || null);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    api.get("/cuentas-bancarias").then((r) => {
      setCuentasBanco(r.data || []);
      if (r.data?.[0]) setPagoForm((f) => ({ ...f, cuenta_bancaria_id: r.data[0].id }));
    }).catch(() => {});
  }, [endpoint]);

  const verDetalle = async (c) => {
    setDetalleLoading(true);
    setDetalle({ contacto: c, movimientos: [], saldo: c.saldo });
    try {
      const { data } = await api.get(`${endpoint}/${c.id}`);
      setDetalle(data);
    } catch (e) {
      toast.error(extractError(e));
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const abrirPago = (c) => {
    setPagoTarget(c);
    setPagoForm((f) => ({
      ...f,
      contacto_rut: c.rut || "",
      monto: c.saldo > 0 ? String(Math.round(c.saldo)) : "",
      glosa: esCliente ? `Cobro ${c.razon_social}` : `Pago ${c.razon_social}`,
    }));
    setPagoOpen(true);
  };

  const submitPago = async () => {
    const monto = parseFloat(pagoForm.monto);
    if (!monto || monto <= 0) {
      toast.warning("Indica un monto válido");
      return;
    }
    if (!pagoForm.cuenta_bancaria_id) {
      toast.warning("Selecciona cuenta bancaria");
      return;
    }
    if (!pagoForm.contacto_rut && !pagoTarget?.id) {
      toast.warning("Indica RUT del contacto");
      return;
    }
    setPagoSaving(true);
    try {
      const payload = {
        contacto_id: pagoTarget?.id,
        contacto_rut: pagoForm.contacto_rut,
        cuenta_bancaria_id: pagoForm.cuenta_bancaria_id,
        fecha: pagoForm.fecha,
        monto,
        glosa: pagoForm.glosa,
        medio_pago: pagoForm.medio_pago,
        n_doc: pagoForm.n_doc,
        imputar_automatico: true,
      };
      await api.post(pagoEndpoint, payload);
      toast.success(esCliente ? "Cobro registrado" : "Pago registrado");
      setPagoOpen(false);
      load();
      if (detalle?.contacto?.id === pagoTarget?.id) verDetalle(pagoTarget);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setPagoSaving(false);
    }
  };

  return (
    <>
      {totales && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <KPI label="Contactos con saldo" value={totales.count} />
          <KPI label="Saldo total" value={fmtCLP(totales.saldo)} tone="primary" />
        </div>
      )}

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#59645E]">Cargando…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#59645E]">
            Sin movimientos en cuenta corriente para {esCliente ? "clientes" : "proveedores"}.
          </div>
        ) : (
          <table className="acc-table" data-testid={`cc-table-${tipo}`}>
            <thead>
              <tr>
                <th>RUT</th>
                <th>Razón social</th>
                <th className="num">{esCliente ? "Facturado" : "Deuda"}</th>
                <th className="num">{esCliente ? "Cobrado" : "Pagado"}</th>
                <th className="num">Saldo</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td className="font-mono text-xs">{c.rut}</td>
                  <td>{c.razon_social}</td>
                  <td className="num">{fmtCLP(esCliente ? c.facturado : c.deuda)}</td>
                  <td className="num">{fmtCLP(esCliente ? c.cobrado : c.pagado)}</td>
                  <td className={`num font-semibold ${c.saldo > 0 ? "text-[#A94438]" : "text-[#2D6A4F]"}`}>
                    {fmtCLP(c.saldo)}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => verDetalle(c)} title="Ver estado de cuenta"
                        className="text-[#1B4332] hover:bg-[#1B4332]/10 p-1 rounded-sm">
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      {c.saldo > 0.5 && (
                        <button onClick={() => abrirPago(c)} title={esCliente ? "Registrar cobro" : "Registrar pago"}
                          className="text-[#2D6A4F] hover:bg-[#2D6A4F]/10 p-1 rounded-sm">
                          {esCliente ? <Wallet className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!detalle} onOpenChange={(v) => !v && setDetalle(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Estado de cuenta · {detalle?.contacto?.razon_social}</DialogTitle>
            <DialogDescription className="font-mono">{detalle?.contacto?.rut}</DialogDescription>
          </DialogHeader>
          {detalleLoading ? (
            <div className="p-6 text-center text-sm text-[#59645E]">Cargando movimientos…</div>
          ) : detalle && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <KPI label="Saldo" value={fmtCLP(detalle.saldo)} tone="primary" />
                {esCliente ? (
                  <>
                    <KPI label="Facturado" value={fmtCLP(detalle.resumen?.facturado)} />
                    <KPI label="Cobrado" value={fmtCLP(detalle.resumen?.cobrado)} />
                  </>
                ) : (
                  <>
                    <KPI label="Deuda" value={fmtCLP(detalle.resumen?.deuda)} />
                    <KPI label="Pagado" value={fmtCLP(detalle.resumen?.pagado)} />
                  </>
                )}
              </div>
              <table className="acc-table text-sm">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Ref.</th><th>Glosa</th>
                    <th className="num">Cargo</th><th className="num">Abono</th><th className="num">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {(detalle.movimientos || []).map((m, i) => (
                    <tr key={i}>
                      <td>{fmtDate(m.fecha)}</td>
                      <td className="font-mono text-xs">{m.referencia}</td>
                      <td>{m.glosa}</td>
                      <td className="num">{m.cargo > 0 ? fmtCLP(m.cargo) : ""}</td>
                      <td className="num">{m.abono > 0 ? fmtCLP(m.abono) : ""}</td>
                      <td className="num font-medium">{fmtCLP(m.saldo_acumulado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={pagoOpen} onOpenChange={(v) => !pagoSaving && setPagoOpen(v)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{esCliente ? "Cobro por RUT" : "Pago por RUT"}</DialogTitle>
            <DialogDescription>
              Imputa automáticamente a documentos pendientes (más antiguos primero).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-caps block mb-1">RUT</label>
              <input value={pagoForm.contacto_rut} onChange={(e) => setPagoForm({ ...pagoForm, contacto_rut: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm font-mono" />
            </div>
            <div>
              <label className="label-caps block mb-1">Monto</label>
              <input type="number" value={pagoForm.monto} onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm font-mono" />
            </div>
            <div>
              <label className="label-caps block mb-1">Cuenta bancaria</label>
              <Select value={pagoForm.cuenta_bancaria_id} onValueChange={(v) => setPagoForm({ ...pagoForm, cuenta_bancaria_id: v })}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {cuentasBanco.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.banco} · {c.numero}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Fecha</label>
                <input type="date" value={pagoForm.fecha} onChange={(e) => setPagoForm({ ...pagoForm, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-caps block mb-1">Medio</label>
                <Select value={pagoForm.medio_pago} onValueChange={(v) => setPagoForm({ ...pagoForm, medio_pago: v })}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1">Glosa</label>
              <input value={pagoForm.glosa} onChange={(e) => setPagoForm({ ...pagoForm, glosa: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm" />
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setPagoOpen(false)} className="border border-[#E2DFD8] text-sm px-4 py-2 rounded-sm">Cancelar</button>
            <button type="button" onClick={submitPago} disabled={pagoSaving}
              className="bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-50 text-white text-sm px-4 py-2 rounded-sm">
              {pagoSaving ? "Registrando…" : esCliente ? "Registrar cobro" : "Registrar pago"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = { default: "text-[#1C211F]", primary: "text-[#1B4332]" };
  return (
    <div className="bg-white border border-[#E2DFD8] rounded-md p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function CuentaCorriente() {
  const [tab, setTab] = useState("cliente");

  return (
    <Page>
      <PageHeader
        title="Cuenta corriente"
        subtitle="Estado de deuda y cobros/pagos por RUT de clientes y proveedores"
        testid="cuenta-corriente-header"
      />
      <div className="flex gap-2 mb-4">
        {[
          { id: "cliente", label: "Clientes" },
          { id: "proveedor", label: "Proveedores" },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            data-testid={`cc-tab-${t.id}`}
            className={`text-sm px-4 py-2 rounded-sm border ${
              tab === t.id ? "bg-[#1B4332] text-white border-[#1B4332]" : "border-[#E2DFD8] text-[#59645E]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      <CuentaCorrientePanel key={tab} tipo={tab} />
    </Page>
  );
}
